import { ja } from 'date-fns/locale';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import MicroModal from 'micromodal';
import { FC, Fragment, useEffect, useState } from 'react';
import { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { getModalOption, Setting, statusMessages } from '../App';
import { Input, Label, Textarea } from '../components/CancelModalCss';
import { useSubscriptionDetailCancel } from '../hooks/use-subscription-cancel';
import base from '../styles/Base.module.css';
import style from '../styles/SubscriptionCancel.module.css';

// date-fns v2 Locale を react-datepicker の期待型へ暫定キャスト
registerLocale('ja', {
  ...ja,
  options: ja.options ?? { weekStartsOn: 0, firstWeekContainsDate: 1 },
} as any);

export type StatusMessage = keyof typeof statusMessages;

dayjs.extend(weekday);

export const SubscriptionCancel: FC<{ setting: Setting }> = ({ setting }) => {
  const [showReasonError, setShowReasonError] = useState(false);

  useEffect(() => {
    window.parent.postMessage({ action: 'subscriptionCancel' }, '*');
  }, []);

  const { subscription, updateStatus, cancelSurveySetting, cancelSurveyInput, setCancelSurveyInput } =
    useSubscriptionDetailCancel(setting);

  // エラー注記の表示/非表示で高さが変わるため、iframe 高さを再送して最下部の注記が
  // クリップされないようにする
  useEffect(() => {
    setting.resize(document.querySelector('body')!.scrollHeight + 100);
  }, [showReasonError, setting]);

  const navigate = useNavigate();

  if (subscription && subscription.status === 'CANCELLED') {
    return (
      <>
        <h2 className={style.cancelHeading}>定期便の解約</h2>
        <main id="modal-cancelled-content">
          <p>既に定期便は解約されています。</p>
        </main>
      </>
    );
  }

  // 解約時アンケートにチェックが入っていて、理由の選択肢が一つ以上設定されていれば解約アンケートが有効
  const cancelSurveyEnable =
    cancelSurveySetting.enable && cancelSurveySetting.reasons && cancelSurveySetting.reasons.length > 0 ? true : false;

  const setCancelReason = (reason: string) => {
    const target = reason === cancelSurveyInput.selected ? undefined : reason;
    setCancelSurveyInput({ ...cancelSurveyInput, selected: target });

    if (target) setShowReasonError(false);
  };

  const cancelWithValidate = () => {
    if (cancelSurveyEnable && cancelSurveySetting.require && !cancelSurveyInput.selected) {
      setShowReasonError(true);
      return;
    }
    setShowReasonError(false);
    MicroModal.show('modal-cancelled-complete', getModalOption(setting));
    updateStatus('cancelled');
  };

  const select = cancelSurveySetting.reasons
    ? cancelSurveySetting.reasons.map((reason, index) => {
        return (
          <Fragment key={index}>
            <Input
              type="radio"
              name="reason"
              id={index.toString()}
              value={reason}
              //onChange={() => setCancelSurveyInput({...cancelSurveyInput, selected: reason})}
              onClick={() => setCancelReason(reason)}
              checked={cancelSurveyInput.selected === reason}
            ></Input>
            <Label htmlFor={index.toString()} className={style.selectLabel}>
              {reason}
            </Label>
          </Fragment>
        );
      })
    : [];

  const cancelMainBody = cancelSurveyEnable ? (
    <main id="modal-cancelled-content" className={style.cancelMainBody}>
      {cancelSurveySetting.leadText ? (
        <p>
          {cancelSurveySetting.leadText.split('\n').map((str) => (
            <>
              {str}
              <br />
            </>
          ))}
        </p>
      ) : (
        <></>
      )}
      <div id="selectButton">{select}</div>
      {cancelSurveySetting.extraField ? (
        <>
          <br />
          <h3>
            <b>その他、ご意見やご要望をご記入ください</b>
          </h3>
          <Textarea
            onChange={(e) => setCancelSurveyInput({ ...cancelSurveyInput, extraField: e.target.value })}
            rows={5}
          ></Textarea>
        </>
      ) : (
        <></>
      )}
    </main>
  ) : (
    <main id="modal-cancelled-content">
      <p>定期便を解約します。</p>
    </main>
  );

  return (
    <>
      <h2 className={style.cancelHeading}>定期便の解約</h2>
      {cancelMainBody}
      <footer className="subscription-cancel-footer">
        <button
          className={style.submitBtn}
          onClick={() => {
            cancelWithValidate();
          }}
        >
          解約する
        </button>
        {showReasonError && <p className={style.errorNote}>※解約理由を選択してください</p>}
        <p className={style.note}>※ 既に決済が完了したご注文はキャンセルされません</p>
      </footer>

      {/* 解約完了モーダル */}
      <div className="micromodal" id="modal-cancelled-complete" aria-hidden="true">
        <div className="micromodal-overlay" tabIndex={-1}>
          <div role="dialog" aria-modal="true" aria-labelledby="modal-cancelled-complete-title">
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" data-micromodal-close></button>
              <h2 id="modal-cancelled-complete-title" className={base.ModalTitle}>
                定期便の解約
              </h2>
            </header>
            <main id="modal-cancelled-complete-content" className={base.ModalContent}>
              <p>
                定期便を解約しました。
                <br />
                再度ご注文される場合は、もう一度定期便をご注文ください。
                <br />
                なお、すでに決済が完了した注文はキャンセルされません。
                <br />
              </p>
            </main>
            <footer>
              <button
                className={`${base.sectionBtn} ${base.ModalButton}`}
                data-micromodal-close
                onClick={() => {
                  navigate(`/`);
                }}
              >
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};
